import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "react-toastify";
import api from "../services/api";
import { fileService } from "../services";
import { useAuth } from "../context/AuthContext";
import {
  Description,
  Add,
  Download,
  Delete,
  Warning,
  FilterList,
  Person,
  Edit,
  Search,
  ViewModule,
  ViewList,
} from "@mui/icons-material";
import Input from "../components/ui/Input";
import Button from "../components/ui/Button";

export default function Documents() {
  const navigate = useNavigate();
  const { user } = useAuth();
  // Check if user is EMPLOYEE (handle both old and new role formats)
  const userRole = user?.role?.toUpperCase() || user?.role;
  const isEmployee = userRole === "EMPLOYEE" || user?.role === "employee";
  const isAdmin = userRole === "ADMIN" || userRole === "HR";
  const isManager = userRole === "MANAGER" || user?.role === "manager";
  
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [employees, setEmployees] = useState([]);
  const [currentEmployee, setCurrentEmployee] = useState(null);
  const [filter, setFilter] = useState({ 
    category: "all", 
    expired: false,
    employeeId: "" 
  });
  const [viewMode, setViewMode] = useState("grid"); // "grid" or "userwise" for managers
  const [userSearchQuery, setUserSearchQuery] = useState(""); // For searching users by name

  useEffect(() => {
    if (isAdmin || isManager) {
      fetchEmployees();
    } else if (isEmployee) {
      fetchCurrentEmployee();
    }
  }, [isAdmin, isManager, isEmployee]);

  useEffect(() => {
    fetchDocuments();
  }, [filter]);

  const fetchEmployees = async () => {
    try {
      const response = await api.get("/employees");
      const payload = response.data;
      const employeeList = Array.isArray(payload)
        ? payload
        : Array.isArray(payload?.data)
        ? payload.data
        : [];
      setEmployees(employeeList);
    } catch (err) {
      console.error("Failed to fetch employees", err);
    }
  };

  const fetchCurrentEmployee = async () => {
    try {
      const response = await api.get("/employees");
      const payload = response.data;
      const employeeList = Array.isArray(payload)
        ? payload
        : Array.isArray(payload?.data)
        ? payload.data
        : [];
      // Find current user's employee record
      const currentUserId = user?.id || user?._id;
      const emp = employeeList.find(
        (e) => (e.user_id?.id || e.user_id?._id || e.user_id)?.toString() === currentUserId?.toString()
      );
      setCurrentEmployee(emp);
    } catch (err) {
      console.error("Failed to fetch current employee", err);
    }
  };

  const fetchDocuments = async () => {
    try {
      setLoading(true);
      const params = {};
      if (filter.category !== "all") params.category = filter.category;
      if (filter.expired) params.is_expired = "true";
      if (filter.employeeId && isAdmin) params.employee_id = filter.employeeId;
      const response = await api.get("/documents", { params });
      setDocuments(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      console.error("Failed to fetch documents", err);
      toast.error("Failed to load documents");
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key) => (e) => {
    setFilter({ ...filter, [key]: e.target.value });
  };

  const handleResetFilters = () => {
    setFilter({ category: "all", expired: false, employeeId: "" });
    setUserSearchQuery("");
  };

  // Group documents by employee for user-wise view
  const groupDocumentsByEmployee = (docs) => {
    const grouped = {};
    docs.forEach((doc) => {
      const empId = doc.employee_id?.id || doc.employee_id?._id || doc.employee_id || "unknown";
      const empName = doc.employee_id?.user_id?.name || "Unknown Employee";
      
      if (!grouped[empId]) {
        grouped[empId] = {
          employeeId: empId,
          employeeName: empName,
          employee: doc.employee_id,
          documents: [],
        };
      }
      grouped[empId].documents.push(doc);
    });
    return Object.values(grouped);
  };

  // Get documents grouped by employee (for user-wise view)
  const documentsByEmployee = groupDocumentsByEmployee(documents);
  
  // Filter grouped documents by search query
  const filteredDocumentsByEmployee = documentsByEmployee.filter((group) => {
    if (!userSearchQuery) return true;
    return group.employeeName.toLowerCase().includes(userSearchQuery.toLowerCase());
  });

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this document?"))
      return;
    try {
      await api.delete(`/documents/${id}`);
      toast.success("Document deleted successfully");
      fetchDocuments();
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to delete document");
    }
  };

  const handleEdit = (doc) => {
    const docId = doc.id || doc._id;
    if (!docId) {
      toast.error("Invalid document ID");
      return;
    }
    navigate(`/documents/${docId}/edit`);
  };

  // Check if user can edit/delete a document
  const canEditOrDelete = (doc) => {
    // Admins and Managers can always edit/delete all documents
    if (isAdmin || isManager) return true;
    
    // Employees can only edit/delete their own documents that they uploaded
    if (isEmployee && currentEmployee) {
      // Check if document belongs to current user's employee record
      const currentEmployeeId = (currentEmployee.id || currentEmployee._id)?.toString();
      const docEmployeeId = (doc.employee_id?.id || doc.employee_id?._id || doc.employee_id || "").toString();
      
      // Document must belong to current employee
      if (docEmployeeId !== currentEmployeeId) {
        return false;
      }
      
      // Check uploader role - cannot edit/delete if uploaded by admin/HR
      const uploaderRole = doc.uploaded_by?.role?.toUpperCase() || doc.uploaded_by?.role;
      if (uploaderRole === "ADMIN" || uploaderRole === "HR") {
        return false;
      }
      
      // Check if current user uploaded it
      const currentUserId = (user?.id || user?._id)?.toString();
      const docUploadedBy = (doc.uploaded_by?.id || doc.uploaded_by?._id || doc.uploaded_by || "").toString();
      
      // Can edit/delete if they uploaded it themselves
      if (docUploadedBy && docUploadedBy === currentUserId) {
        return true;
      }
    }
    
    return false;
  };

  const getCategoryColor = (category) => {
    const colors = {
      hr: "bg-blue-100 text-blue-700",
      finance: "bg-green-100 text-green-700",
      compliance: "bg-orange-100 text-orange-700",
      legal: "bg-purple-100 text-purple-700",
      other: "bg-gray-100 text-gray-700",
    };
    return colors[category] || colors.other;
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6 sm:mb-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4"
        >
          <div className="flex-1 min-w-0">
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-primary mb-2">
              Document Vault
            </h1>
            <p className="text-sm sm:text-base text-gray-600">
              {isEmployee ? "View your documents" : "Manage employee documents securely"}
            </p>
          </div>
          <button
            onClick={() => navigate("/documents/upload")}
            className="btn-primary flex items-center justify-center gap-2 w-full sm:w-auto px-4 py-2 sm:px-6 sm:py-3 text-sm sm:text-base"
          >
            <Add className="w-4 h-4 sm:w-5 sm:h-5" />
            <span>Upload Document</span>
          </button>
        </motion.div>

      {/* View Mode Toggle (Manager only) */}
      {isManager && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.05 }}
          className="mb-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4"
        >
          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold text-gray-700">View:</span>
            <div className="flex gap-2 bg-gray-100 rounded-lg p-1">
              <button
                onClick={() => setViewMode("grid")}
                className={`px-3 sm:px-4 py-2 rounded-md text-xs sm:text-sm font-semibold transition-all flex items-center gap-2 ${
                  viewMode === "grid"
                    ? "bg-primary text-white"
                    : "text-gray-700 hover:bg-gray-200"
                }`}
              >
                <ViewModule className="w-4 h-4" />
                <span className="hidden sm:inline">Grid</span>
              </button>
              <button
                onClick={() => setViewMode("userwise")}
                className={`px-3 sm:px-4 py-2 rounded-md text-xs sm:text-sm font-semibold transition-all flex items-center gap-2 ${
                  viewMode === "userwise"
                    ? "bg-primary text-white"
                    : "text-gray-700 hover:bg-gray-200"
                }`}
              >
                <ViewList className="w-4 h-4" />
                <span className="hidden sm:inline">User-wise</span>
                <span className="sm:hidden">Users</span>
              </button>
            </div>
          </div>
          {viewMode === "userwise" && (
            <div className="w-full sm:w-64">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 text-gray-400" />
                <Input
                  type="text"
                  placeholder="Search by user name..."
                  value={userSearchQuery}
                  onChange={(e) => setUserSearchQuery(e.target.value)}
                  className="pl-9 sm:pl-10 w-full text-sm sm:text-base"
                />
              </div>
            </div>
          )}
        </motion.div>
      )}

      {/* Filters */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="card mb-6 p-4 sm:p-6"
      >
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <FilterList className="w-5 h-5 text-gray-400" />
              <label className="text-sm font-semibold text-gray-700">
                Category
              </label>
            </div>
            <select
              value={filter.category}
              onChange={handleFilterChange("category")}
              className="input-field w-full"
            >
              {["all", "hr", "finance", "compliance", "legal"].map((cat) => (
                <option key={cat} value={cat}>
                  {cat === "all" ? "All Categories" : cat.toUpperCase()}
                </option>
              ))}
            </select>
          </div>
          {isAdmin && (
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Person className="w-5 h-5 text-gray-400" />
                <label className="text-sm font-semibold text-gray-700">
                  Employee
                </label>
              </div>
              <select
                value={filter.employeeId}
                onChange={handleFilterChange("employeeId")}
                className="input-field w-full"
              >
                <option value="">All Employees</option>
                {employees.map((employee) => (
                  <option key={employee._id || employee.id} value={employee._id || employee.id}>
                    {employee.user_id?.name || "Unknown"}
                  </option>
                ))}
              </select>
            </div>
          )}
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Warning className="w-5 h-5 text-gray-400" />
              <label className="text-sm font-semibold text-gray-700">
                Status
              </label>
            </div>
            <button
              onClick={() => setFilter({ ...filter, expired: !filter.expired })}
              className={`w-full px-4 py-2 rounded-lg text-sm font-semibold transition-all flex items-center justify-center gap-2 ${
                filter.expired
                  ? "bg-red-100 text-red-700"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }`}
            >
              <Warning className="w-4 h-4" />
              {filter.expired ? "Expired Only" : "Show All"}
            </button>
          </div>
        </div>
        <div className="mt-4 flex justify-end">
          <Button variant="secondary" onClick={handleResetFilters}>
            Clear Filters
          </Button>
        </div>
      </motion.div>

      {/* Documents View */}
      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <div key={i} className="card animate-pulse p-4 sm:p-6">
              <div className="h-6 bg-gray-200 rounded w-3/4 mb-4"></div>
              <div className="h-4 bg-gray-200 rounded w-1/2"></div>
            </div>
          ))}
        </div>
      ) : documents.length === 0 ? (
        <div className="card text-center py-12 p-4 sm:p-6">
          <Description className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600 text-lg mb-4">No documents found</p>
          <button
            onClick={() => navigate("/documents/upload")}
            className="btn-primary"
          >
            Upload First Document
          </button>
        </div>
      ) : viewMode === "userwise" && isManager ? (
        /* User-wise Card View (Manager) */
        <div className="space-y-4 sm:space-y-6">
          {filteredDocumentsByEmployee.length === 0 ? (
            <div className="card text-center py-12 p-4 sm:p-6">
              <Person className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600 text-lg mb-2">No users found</p>
              <p className="text-gray-500 text-sm">Try adjusting your search query</p>
            </div>
          ) : (
            filteredDocumentsByEmployee.map((group, groupIndex) => (
              <motion.div
                key={group.employeeId}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: groupIndex * 0.05 }}
                className="card p-4 sm:p-6"
              >
                {/* User Header */}
                <div className="flex items-center gap-3 mb-4 pb-4 border-b border-gray-200">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                    <Person className="w-5 h-5 sm:w-6 sm:h-6 text-primary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-lg sm:text-xl font-bold text-primary truncate">
                      {group.employeeName}
                    </h3>
                    <p className="text-xs sm:text-sm text-gray-500">
                      {group.documents.length} {group.documents.length === 1 ? "document" : "documents"}
                    </p>
                  </div>
                </div>

                {/* Documents List for this User */}
                {group.documents.length === 0 ? (
                  <p className="text-sm text-gray-500 text-center py-4">No documents</p>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                    {group.documents.map((doc, docIndex) => (
                      <motion.div
                        key={doc.id || doc._id}
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: (groupIndex * 0.05) + (docIndex * 0.02) }}
                        className="border border-gray-200 rounded-lg p-3 sm:p-4 hover:shadow-md transition-all bg-white"
                      >
                        <div className="flex items-start justify-between mb-2 gap-2">
                          <div className="flex-1 min-w-0">
                            <h4 className="text-sm sm:text-base font-semibold text-primary mb-1 truncate">
                              {doc.title}
                            </h4>
                            <div className="flex items-center gap-2 mb-2 flex-wrap">
                              <span
                                className={`px-2 py-1 rounded text-xs font-semibold ${getCategoryColor(
                                  doc.category
                                )}`}
                              >
                                {doc.category.toUpperCase()}
                              </span>
                              {doc.is_expired && (
                                <span className="px-2 py-1 rounded text-xs font-semibold bg-red-100 text-red-700 flex items-center gap-1">
                                  <Warning className="w-3 h-3" />
                                  Expired
                                </span>
                              )}
                            </div>
                            <p className="text-xs text-gray-600 truncate">
                              {doc.document_type?.replace(/_/g, " ").toUpperCase()}
                            </p>
                            {doc.expiry_date && (
                              <p className="text-xs text-gray-500 mt-1">
                                Expires: {new Date(doc.expiry_date).toLocaleDateString()}
                              </p>
                            )}
                          </div>
                        </div>
                        <div className="flex flex-col sm:flex-row gap-2 mt-3">
                          {doc.file_url && (
                            <button
                              type="button"
                              onClick={() => fileService.openDownload(doc.file_url, (err) => toast.error(err))}
                              className="flex-1 px-3 py-2 bg-primary text-white rounded-lg font-semibold text-center hover:bg-primary/90 transition-all flex items-center justify-center gap-2 text-xs sm:text-sm"
                            >
                              <Download className="w-3 h-3 sm:w-4 sm:h-4" />
                              <span>Download</span>
                            </button>
                          )}
                          {canEditOrDelete(doc) && (
                            <div className="flex gap-2">
                              <button
                                onClick={() => handleEdit(doc)}
                                className="flex-1 sm:flex-initial px-3 py-2 bg-blue-100 text-blue-700 rounded-lg font-semibold hover:bg-blue-200 transition-all flex items-center justify-center gap-2 text-xs sm:text-sm"
                                title="Edit document"
                              >
                                <Edit className="w-3 h-3 sm:w-4 sm:h-4" />
                                <span className="sm:hidden">Edit</span>
                              </button>
                              <button
                                onClick={() => handleDelete(doc._id || doc.id)}
                                className="flex-1 sm:flex-initial px-3 py-2 bg-red-100 text-red-700 rounded-lg font-semibold hover:bg-red-200 transition-all flex items-center justify-center gap-2 text-xs sm:text-sm"
                                title="Delete document"
                              >
                                <Delete className="w-3 h-3 sm:w-4 sm:h-4" />
                                <span className="sm:hidden">Delete</span>
                              </button>
                            </div>
                          )}
                        </div>
                      </motion.div>
                    ))}
                  </div>
                )}
              </motion.div>
            ))
          )}
        </div>
      ) : (
        /* Grid View (Default) */
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
          {documents.map((doc, index) => (
            <motion.div
              key={doc.id || doc._id}
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: index * 0.05 }}
              className="card hover:shadow-soft-lg transition-all p-4 sm:p-6"
            >
              <div className="flex items-start justify-between mb-4 gap-2">
                <div className="flex-1 min-w-0">
                  <h3 className="text-base sm:text-lg font-bold text-primary mb-2 truncate">
                    {doc.title}
                  </h3>
                  <div className="flex items-center gap-2 mb-2">
                    <span
                      className={`px-2 py-1 rounded-lg text-xs font-semibold ${getCategoryColor(
                        doc.category
                      )}`}
                    >
                      {doc.category.toUpperCase()}
                    </span>
                    {doc.is_expired && (
                      <span className="px-2 py-1 rounded-lg text-xs font-semibold bg-red-100 text-red-700 flex items-center gap-1">
                        <Warning className="w-3 h-3" />
                        Expired
                      </span>
                    )}
                  </div>
                  <p className="text-xs sm:text-sm text-gray-600 mb-2 truncate">
                    Employee: {doc.employee_id?.user_id?.name || "N/A"}
                  </p>
                  <p className="text-xs sm:text-sm text-gray-600 truncate">
                    Type: {doc.document_type?.replace(/_/g, " ").toUpperCase()}
                  </p>
                  {doc.expiry_date && (
                    <p className="text-xs sm:text-sm text-gray-600 mt-1">
                      Expires: {new Date(doc.expiry_date).toLocaleDateString()}
                    </p>
                  )}
                </div>
              </div>
              <div className="flex flex-col sm:flex-row gap-2 mt-4">
                {doc.file_url && (
                  <button
                    type="button"
                    onClick={() => fileService.openDownload(doc.file_url, (err) => toast.error(err))}
                    className="flex-1 px-3 sm:px-4 py-2 bg-primary text-white rounded-xl font-semibold text-center hover:bg-primary/90 transition-all flex items-center justify-center gap-2 text-sm sm:text-base"
                  >
                    <Download className="w-4 h-4" />
                    <span>Download</span>
                  </button>
                )}
                {canEditOrDelete(doc) && (
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleEdit(doc)}
                      className="flex-1 sm:flex-initial px-3 sm:px-4 py-2 bg-blue-100 text-blue-700 rounded-xl font-semibold hover:bg-blue-200 transition-all flex items-center justify-center gap-2 text-sm sm:text-base"
                      title="Edit document"
                    >
                      <Edit className="w-4 h-4" />
                      <span className="sm:hidden">Edit</span>
                    </button>
                    <button
                      onClick={() => handleDelete(doc._id || doc.id)}
                      className="flex-1 sm:flex-initial px-3 sm:px-4 py-2 bg-red-100 text-red-700 rounded-xl font-semibold hover:bg-red-200 transition-all flex items-center justify-center gap-2 text-sm sm:text-base"
                      title="Delete document"
                    >
                      <Delete className="w-4 h-4" />
                      <span className="sm:hidden">Delete</span>
                    </button>
                  </div>
                )}
              </div>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
}
